import {useParams} from "react-router-dom";
import {Table, Tag, Tabs, Empty, Alert} from "antd";
import className from "classnames/bind";
import {ExportButtons, FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetCrawlCompareQuery} from "~/reduxs/api/crawlerApiSlice";
import {scoreColor} from "../components/SiteScoreCards";
import style from "../style/SiteCrawler.module.scss";

const cn = className.bind(style);

const SCORE_LABELS = {
	avg_score: 'Điểm tổng',
	score_technical: 'Kỹ thuật & Index',
	score_meta: 'Thẻ Meta',
	score_social: 'Mạng xã hội',
	score_content: 'Nội dung',
	score_image: 'Hình ảnh',
	score_structured: 'Cấu trúc & Link',
};
const SCORE_ORDER = ['avg_score', 'score_technical', 'score_meta', 'score_social', 'score_content', 'score_image', 'score_structured'];

/** Nhãn tiếng Việt cho loại lỗi cấp site (fallback: giữ nguyên type). */
const ISSUE_TYPE_LABELS = {
	duplicate_title: 'Trùng tiêu đề',
	duplicate_meta: 'Trùng meta description',
	duplicate_content: 'Trùng nội dung',
	server_error: 'Lỗi server (5xx)',
	broken_internal_link: 'Link nội bộ hỏng',
	broken_external_link: 'Link ngoài hỏng',
	external_check_capped: 'Giới hạn kiểm tra link ngoài',
	redirect: 'Chuyển hướng',
	redirect_chain: 'Chuỗi chuyển hướng',
	orphan_page: 'Trang mồ côi',
	canonical_cross: 'Canonical chéo',
	robots_blocked: 'Bị chặn bởi robots.txt',
};
const issueTypeLabel = (t) => ISSUE_TYPE_LABELS[t] || t;

/** Ô delta: dương/âm có màu. goodWhenPositive=false cho lỗi (tăng lỗi = xấu). */
function Delta({value, goodWhenPositive = true, suffix = ''}) {
	if (!value) return <span style={{color: '#9ca3af'}}>—</span>;
	const good = goodWhenPositive ? value > 0 : value < 0;
	const color = good ? '#52c41a' : '#f5222d';
	return (
		<span style={{color, fontWeight: 600}}>
			<FontAwesomeIcon icon={value > 0 ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'} />
			{' '}{value > 0 ? '+' : ''}{value}{suffix}
		</span>
	);
}

const statusCodeColor = (code) => {
	if (code >= 200 && code < 300) return 'green';
	if (code >= 300 && code < 400) return 'gold';
	if (code >= 400 && code < 500) return 'orange';
	if (code >= 500) return 'red';
	return 'default';
};

/** Dựng dữ liệu xuất Excel/PDF cho so sánh 2 lần crawl (Excel đầy đủ, PDF cắt 50 dòng/bảng). */
function buildCompareExport({base, target, scores, issues, pages}) {
	const headBT = [`Crawl #${base.id}`, `Crawl #${target.id}`, 'Thay đổi'];

	const scoreRows = SCORE_ORDER.map((key) => [SCORE_LABELS[key], scores[key].base, scores[key].target, scores[key].delta]);

	const SEV_LABEL = {error: 'Lỗi nặng', warning: 'Cảnh báo', info: 'Thông tin'};
	const sevRows = ['error', 'warning', 'info'].map((sev) => {
		const s = issues.by_severity[sev];
		return [SEV_LABEL[sev], s.base, s.target, s.delta];
	});
	const typeRows = issues.by_type.map((t) => [issueTypeLabel(t.type), t.base, t.target, t.delta]);

	const addedRows = pages.added.map((p) => [p.url, p.status_code, p.score]);
	const removedRows = pages.removed.map((p) => [p.url, p.status_code, p.score]);
	const changedHead = ['URL', `Điểm #${base.id}`, `Điểm #${target.id}`, 'Thay đổi'];
	const declinedRows = pages.declined.map((p) => [p.url, p.base_score, p.target_score, p.delta]);
	const improvedRows = pages.improved.map((p) => [p.url, p.base_score, p.target_score, p.delta]);

	const buildExcel = () => [
		{name: 'Điểm theo nhóm', aoa: [['Nhóm', ...headBT], ...scoreRows]},
		{name: 'Lỗi & cảnh báo', aoa: [['Mức độ', ...headBT], ...sevRows, [], ['Loại vấn đề', ...headBT], ...typeRows]},
		{name: 'Trang mới', aoa: [['URL', 'HTTP', 'Điểm'], ...addedRows]},
		{name: 'Trang mất', aoa: [['URL', 'HTTP', 'Điểm'], ...removedRows]},
		{name: 'Tụt điểm', aoa: [changedHead, ...declinedRows]},
		{name: 'Tăng điểm', aoa: [changedHead, ...improvedRows]},
	];

	const cap = (rows) => rows.slice(0, 50);
	const buildPdf = () => ({
		title: `So sánh Crawl #${base.id} → #${target.id}`,
		subtitle: `${base.finished_at || base.started_at || '?'} → ${target.finished_at || target.started_at || '?'}`,
		sections: [
			{heading: 'Điểm SEO theo nhóm', columns: ['Nhóm', ...headBT], rows: scoreRows},
			{heading: 'Lỗi & cảnh báo', columns: ['Mức độ', ...headBT], rows: sevRows},
			...(typeRows.length > 0 ? [{heading: 'Biến động theo loại vấn đề', columns: ['Loại vấn đề', ...headBT], rows: typeRows}] : []),
			{heading: `Trang tụt điểm (${Math.min(declinedRows.length, 50)}/${declinedRows.length})`, columns: changedHead, rows: cap(declinedRows)},
			{heading: `Trang tăng điểm (${Math.min(improvedRows.length, 50)}/${improvedRows.length})`, columns: changedHead, rows: cap(improvedRows)},
			{heading: `Trang mới (${Math.min(addedRows.length, 50)}/${addedRows.length})`, columns: ['URL', 'HTTP', 'Điểm'], rows: cap(addedRows)},
			{heading: `Trang mất (${Math.min(removedRows.length, 50)}/${removedRows.length})`, columns: ['URL', 'HTTP', 'Điểm'], rows: cap(removedRows)},
		],
	});

	return {filename: `so-sanh-crawl-${base.id}-${target.id}`, buildExcel, buildPdf};
}

/**
 * So sánh 2 lần crawl cùng dự án: diff điểm 6 nhóm, số lỗi (severity + theo loại),
 * URL mới/mất, trang tụt/tăng điểm. base = lần cũ hơn, target = lần mới hơn (BE tự sắp).
 */
function CrawlCompare() {

	const {a, b} = useParams();
	const {data, isLoading, isError, error} = useGetCrawlCompareQuery({a, b});

	if (isLoading) return <Loading />;
	if (isError || !data) {
		return <div className="container"><Alert type="error" showIcon message="Không thể so sánh"
			description={rtkErrorMessage(error, 'Hai lần crawl không hợp lệ hoặc không cùng dự án.')} /></div>;
	}

	const {base, target, project_id, scores, issues, pages} = data;

	// ---- Bảng điểm ----
	const scoreRows = SCORE_ORDER.map((key) => ({key, label: SCORE_LABELS[key], ...scores[key]}));

	const scoreColumns = [
		{title: 'Nhóm', dataIndex: 'label', key: 'label', render: (v, r) => (r.key === 'avg_score' ? <strong>{v}</strong> : v)},
		{title: `Crawl #${base.id}`, dataIndex: 'base', key: 'base', align: 'center', width: 120,
			render: (v) => <span style={{color: scoreColor(v), fontWeight: 600}}>{v}</span>},
		{title: `Crawl #${target.id}`, dataIndex: 'target', key: 'target', align: 'center', width: 120,
			render: (v) => <span style={{color: scoreColor(v), fontWeight: 600}}>{v}</span>},
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', align: 'center', width: 110,
			render: (v) => <Delta value={v} goodWhenPositive />},
	];

	// ---- Bảng lỗi theo severity ----
	const SEV_LABEL = {error: 'Lỗi nặng', warning: 'Cảnh báo', info: 'Thông tin'};
	const SEV_COLOR = {error: 'red', warning: 'orange', info: 'blue'};
	const sevRows = ['error', 'warning', 'info'].map((sev) => ({key: sev, ...issues.by_severity[sev]}));

	const sevColumns = [
		{title: 'Mức độ', dataIndex: 'key', key: 'sev', render: (v) => <Tag color={SEV_COLOR[v]}>{SEV_LABEL[v]}</Tag>},
		{title: `Crawl #${base.id}`, dataIndex: 'base', key: 'base', align: 'center', width: 120},
		{title: `Crawl #${target.id}`, dataIndex: 'target', key: 'target', align: 'center', width: 120},
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', align: 'center', width: 110,
			render: (v) => <Delta value={v} goodWhenPositive={false} />},
	];

	// ---- Bảng lỗi theo loại ----
	const typeColumns = [
		{title: 'Loại vấn đề', dataIndex: 'type', key: 'type', render: (v) => issueTypeLabel(v)},
		{title: `Crawl #${base.id}`, dataIndex: 'base', key: 'base', align: 'center', width: 120},
		{title: `Crawl #${target.id}`, dataIndex: 'target', key: 'target', align: 'center', width: 120},
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', align: 'center', width: 110,
			render: (v) => <Delta value={v} goodWhenPositive={false} />},
	];

	// ---- Tabs thay đổi trang ----
	const urlCol = {title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true,
		render: (v) => <a href={v} target="_blank" rel="noreferrer">{v}</a>};
	const scoreCol = (dataIndex, title) => ({title, dataIndex, key: dataIndex, align: 'center', width: 90,
		render: (v) => <span style={{color: scoreColor(v), fontWeight: 600}}>{v}</span>});

	const addedRemovedCols = [
		urlCol,
		{title: 'HTTP', dataIndex: 'status_code', key: 'status', align: 'center', width: 90,
			render: (v) => <Tag color={statusCodeColor(v)}>{v}</Tag>},
		scoreCol('score', 'Điểm'),
	];
	const changedCols = [
		urlCol,
		scoreCol('base_score', `#${base.id}`),
		scoreCol('target_score', `#${target.id}`),
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', align: 'center', width: 110,
			render: (v) => <Delta value={v} goodWhenPositive />},
	];

	const pageTab = (label, count, rows, columns, emptyText) => ({
		key: label,
		label: <span>{label} <Tag>{count}</Tag></span>,
		children: rows.length > 0
			? <Table rowKey="url" size="small" columns={columns} dataSource={rows} scroll={{x: 'max-content'}} pagination={{pageSize: 10, hideOnSinglePage: true}} />
			: <Empty description={emptyText} />,
	});

	const pageTabs = [
		pageTab('Trang mới', pages.added_count, pages.added, addedRemovedCols, 'Không có trang mới.'),
		pageTab('Trang mất', pages.removed_count, pages.removed, addedRemovedCols, 'Không có trang bị mất.'),
		pageTab('Tụt điểm', pages.declined_count, pages.declined, changedCols, 'Không có trang tụt điểm 🎉'),
		pageTab('Tăng điểm', pages.improved_count, pages.improved, changedCols, 'Không có trang tăng điểm.'),
	];

	const avgDelta = scores.avg_score.delta;

	return (
		<div className="container">
			<ProjectToolNav projectId={project_id} active="crawler"
				trail={[{label: 'Site Crawler', to: `/projects/${project_id}/crawler`}, {label: `So sánh #${base.id} ↔ #${target.id}`}]} />

			<PageHeader
				icon="fa-light fa-code-compare"
				title={`So sánh Crawl #${base.id} → #${target.id}`}
				subtitle={`${base.finished_at || base.started_at || '?'}  →  ${target.finished_at || target.started_at || '?'}`}
			/>

			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
				<ExportButtons {...buildCompareExport(data)} />
			</div>

			<div className={cn('section')}>
				<Alert
					type={avgDelta > 0 ? 'success' : avgDelta < 0 ? 'error' : 'info'}
					showIcon
					message={
						avgDelta > 0 ? `Điểm tổng tăng ${avgDelta} điểm — site đã tốt lên.`
							: avgDelta < 0 ? `Điểm tổng giảm ${Math.abs(avgDelta)} điểm — site kém đi.`
								: 'Điểm tổng không đổi.'
					}
					description={`Số trang: ${pages.base_count} → ${pages.target_count} · Tổng vấn đề: ${issues.base_total} → ${issues.target_total} · ${pages.added_count} trang mới, ${pages.removed_count} trang mất.`}
				/>
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Điểm SEO theo nhóm</h3>
				<Table rowKey="key" columns={scoreColumns} dataSource={scoreRows} pagination={false} size="small" scroll={{x: 'max-content'}} />
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Lỗi & cảnh báo</h3>
				<Table rowKey="key" columns={sevColumns} dataSource={sevRows} pagination={false} size="small" scroll={{x: 'max-content'}} />
				{issues.by_type.length > 0 && (
					<>
						<h4 style={{margin: '16px 0 8px', fontSize: 14}}>Biến động theo loại vấn đề</h4>
						<Table rowKey="type" columns={typeColumns} dataSource={issues.by_type}
							pagination={{pageSize: 10, hideOnSinglePage: true}} size="small" scroll={{x: 'max-content'}} />
					</>
				)}
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Thay đổi trang</h3>
				<Tabs items={pageTabs} />
			</div>
		</div>
	);
}

export default CrawlCompare;
